import {
  ConfirmCheckoutMutation,
  ConfirmCheckoutRequest,
  ConfirmCheckoutRequestParams,
  ConfirmedCheckout,
} from '@afterpaytouch/portal-api/consumer/checkout/types'
import { PaymentType } from '@afterpay/types'
import { parseCookies } from 'nookies'
import {
  useCohort,
  useDonation,
  useIsConsumerLendingAutopayEnabled,
  useIsEnabledPBIAutoPay,
  useProfilingId,
  useSelectedPaymentMethod,
  useSelectedPaymentType,
  useConfirmCheckoutMutation,
  useCheckoutData,
} from '../..'
import { PaymentMethodType, THEME_COOKIE_NAME } from '../../../model'
import { app } from '../../../env'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { useCheckoutState, useCheckoutToken } from '../hooks'
import { Theme } from '@afterpaytouch/core'
import { getCheckoutApiVersionForOrderSource } from '../apiVersion'

export const useConfirmCheckoutArgs = (params?: ConfirmCheckoutRequestParams): ConfirmCheckoutMutation => {
  const token = useCheckoutToken()
  const checkout = useCheckoutData()
  const profilingSessionId = useProfilingId()
  const paymentType = useSelectedPaymentType()
  const pclAutopay = useIsConsumerLendingAutopayEnabled()
  const pbiAutopay = useIsEnabledPBIAutoPay()
  const { donationAmount } = useDonation()
  const checkoutCohort = useCohort()

  const { started, creditCheckAgree, termsDisclosureAgree, confirmTermsViewed } = useCheckoutState()
  const selectedPaymentMethod = useSelectedPaymentMethod()

  const { shouldUpdatePreferredPaymentDay = false } = params ?? {}
  const { APP_VERSION } = app

  const cookies = parseCookies()
  const checkoutTheme = cookies[THEME_COOKIE_NAME] as Theme
  const termsViewed = `${confirmTermsViewed ?? ''}-${APP_VERSION}`

  /**
   * If paymentType is undefined, it means the user is in the PBI flow
   * If paymentType is defined, it means the user is in the PCL flow, but the paymentType can be PCL or PBI
   */
  const autoPayMap: Record<PaymentType, boolean | undefined> = {
    [PaymentType.PBI]: pbiAutopay,
    [PaymentType.PCL]: pclAutopay,
    [PaymentType.PAD]: undefined,
  }
  const autopay = paymentType ? autoPayMap[paymentType] : pbiAutopay

  const donationCampaign = started?.donationCampaign

  const isBankAccountOrCashAppPay =
    typeof selectedPaymentMethod !== 'undefined' &&
    (selectedPaymentMethod?.type === PaymentMethodType.ACH_BANK_ACCOUNT || selectedPaymentMethod?.type === PaymentMethodType.CASH_APP_PAY)

  const bankAccountOrCashAppPayObject = (): { paymentMethodId: number; paymentMethodType: PaymentMethodType } | undefined => {
    if (!isBankAccountOrCashAppPay || typeof selectedPaymentMethod === 'undefined') {
      return
    }

    const paymentMethodId = 'account' in selectedPaymentMethod ? selectedPaymentMethod.account.id : selectedPaymentMethod.cashAppPay.id
    return {
      paymentMethodId,
      paymentMethodType: selectedPaymentMethod.type,
    }
  }

  const checkoutClient = 'checkout-redesign-v1'
  const body: ConfirmCheckoutRequest = {
    profilingSessionId,
    checkoutClient,
    paymentType,
    checkoutCohort,
    checkoutTheme,
    ...(donationAmount ? { donation: donationCampaign } : null),
    autopay,
    creditCheckAgree,
    ...(shouldUpdatePreferredPaymentDay && { shouldUpdatePreferredPaymentDay }),
    ...bankAccountOrCashAppPayObject(),
    termsDisclosureAgree,
    ...(valueIsNotNullOrUndefined(started?.accountOpeningDisclosureConsent) && {
      accountOpeningDisclosureConsent: started?.accountOpeningDisclosureConsent,
      termsViewed,
    }),
  }

  const headers =
    typeof window !== 'undefined' && window.xprops && typeof window.xprops.apAppDeviceId === 'string' ? { 'x-ap-device-id': window.xprops.apAppDeviceId } : {}

  const apiVersion = getCheckoutApiVersionForOrderSource(checkout?.merchant?.source)
  return {
    token,
    body,
    headers,
    ...(apiVersion === 'v2' && { apiVersion }),
  }
}

export const useConfirmCheckout = (): ((params?: ConfirmCheckoutRequestParams) => Promise<ConfirmedCheckout>) => {
  const [confirmCheckoutMutation] = useConfirmCheckoutMutation()
  const baseConfirmArgs = useConfirmCheckoutArgs()

  return async (params) => {
    const { shouldUpdatePreferredPaymentDay = false } = params ?? {}
    const confirmArgs = shouldUpdatePreferredPaymentDay
      ? {
          ...baseConfirmArgs,
          body: {
            ...baseConfirmArgs.body,
            shouldUpdatePreferredPaymentDay: true,
          },
        }
      : baseConfirmArgs

    return await confirmCheckoutMutation(confirmArgs).unwrap()
  }
}
